import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import {
  otpAuthSlice,
  siteSlice,
  userSlice,
  notificationsSlice,
  createProjectSlice,
} from "./Slice/slices";
import {
  addressApi,
  agreementApi,
  authApi,
  blogDataApi,
  commentsApi,
  customerApi,
  faqApi,
  InviteApi,
  offerApi,
  orderApi,
  projectApi,
  providerApi,
  providerDocsApi,
  providerServiceAreaApi,
  requestServiceApi,
  serviceApi,
  servicesQuestionApi,
  skillsApi,
  ticketApi,
  ticketMessageApi,
  uploadApi,
  walletApi,
  notificationsApi,
  discountApi,
  chatApi,
  seoApi,
  staticApi,
  supportApi,
  PromoteApi,
  settlementApi,
  ProviderAvailabilityApi,
  commissionApi,
  appVersionApi,
} from "./Slice/api";

import { history } from "../utils/history";
import { isRejectedWithValue } from "@reduxjs/toolkit";
// ========== Middleware سفارشی برای مدیریت خطاهای 500 و 502 ==========
let globalRouter = null;
export const setGlobalRouter = (router) => {
  globalRouter = router;
};

// ================= SERVER ERROR HANDLER =================

let isOnErrorPage = false;
let healthCheckInterval = null;
let lastVisitedPage = "/";

// detect all server/network related errors
const isServerError = (status) => {
  return (
    status === "FETCH_ERROR" || // no internet / DNS / server down
    status === "PARSING_ERROR" ||
    status === "TIMEOUT_ERROR" ||
    status === "CORS" ||
    // status === 500 ||  
    status === 501 ||
    status === 502 ||
    status === 503 ||
    status === 504
  );
};

// background health checker
const startHealthCheck = () => {
  // don't create multiple intervals
  if (healthCheckInterval) return;

  healthCheckInterval = setInterval(async () => {
    try {
      // use a lightweight endpoint
      // change this to your real API health endpoint
      const res = await fetch(
        `${process.env.REACT_APP_API || process.env.EXPO_PUBLIC_API}/health`,
        {
          method: "GET",
          cache: "no-store",
        },
      );

      // server is back
      if (res.ok) {
        clearInterval(healthCheckInterval);
        healthCheckInterval = null;
        isOnErrorPage = false;

        // return user to previous page
        window.location.replace(lastVisitedPage || "/");
      }
    } catch (err) {
      // still down - do nothing
    }
  }, 60000); // check every 60 seconds
};

const rtkErrorMiddleware = () => (next) => (action) => {
  const result = next(action);

  if (isRejectedWithValue(action)) {
    const status = action.payload?.status || action.error?.status;

    if (isServerError(status)) {
      // redirect only once
      if (!isOnErrorPage) {
        isOnErrorPage = true;

        // remember current page
        lastVisitedPage =
          window.location.pathname +
          window.location.search +
          window.location.hash;

        // avoid saving the error page itself
        if (lastVisitedPage === "/server-error") {
          lastVisitedPage = "/";
        }

        // go to error page
        window.location.replace("/server-error");

        // start background checking
        // startHealthCheck();
      }
    }
  }

  return result;
};
// ========== Middleware سفارشی برای مدیریت خودکار سوکت بر اساس توکن ==========
const socketMiddleware = (store) => {
  let currentSocket = null;
  let currentToken = null;

  const closeSocket = () => {
    if (currentSocket) {
      import("../utils/socket").then(({ disconnectSocket }) => {
        disconnectSocket();
      });
      currentSocket = null;
      currentToken = null;
    }
  };

  const initSocket = (token) => {
    if (!token) return;
    if (currentSocket && currentToken === token) return; // قبلاً متصل است
    closeSocket();
    import("../utils/socket").then(({ connectSocket }) => {
      currentSocket = connectSocket(token);
      currentToken = token;

      currentSocket.on("new_notification", (notification) => {
        store.dispatch({
          type: "notifications/addNotification",
          payload: notification,
        });
        store.dispatch(notificationsApi.util.invalidateTags(["Notification"]));
        store.dispatch(notificationsApi.util.invalidateTags(["UnreadCount"]));
      });

      currentSocket.on("new_service_request", (notification) => {
        store.dispatch({
          type: "notifications/addNotification",
          payload: notification,
        });
        store.dispatch(notificationsApi.util.invalidateTags(["Notification"]));
        store.dispatch(notificationsApi.util.invalidateTags(["UnreadCount"]));
      });

      currentSocket.on("unread_notifications", (list) => {
        store.dispatch({
          type: "notifications/setUnreadList",
          payload: list,
        });
      });
    });
  };

  return (next) => (action) => {
    const result = next(action);
    // پس از هر action، وضعیت توکن را از userSlice می‌خوانیم
    const state = store.getState();
    const newToken = state.userSlice?.token;
    if (newToken && newToken !== currentToken) {
      initSocket(newToken);
    } else if (!newToken && currentToken) {
      closeSocket();
    }
    return result;
  };
};

// persist config
const persistConfig = {
  key: "root",
  storage,
  whitelist: ["userSlice", "authSlice", "createProjectSlice"],
};

const rootReducer = combineReducers({
  authSlice: otpAuthSlice,
  userSlice: userSlice,
  siteSlice: siteSlice,
  createProjectSlice: createProjectSlice,
  notifications: notificationsSlice,

  [authApi.reducerPath]: authApi.reducer,
  [serviceApi.reducerPath]: serviceApi.reducer,
  [uploadApi.reducerPath]: uploadApi.reducer,
  [providerApi.reducerPath]: providerApi.reducer,
  [addressApi.reducerPath]: addressApi.reducer,
  [faqApi.reducerPath]: faqApi.reducer,
  [providerDocsApi.reducerPath]: providerDocsApi.reducer,
  [customerApi.reducerPath]: customerApi.reducer,
  [InviteApi.reducerPath]: InviteApi.reducer,
  [requestServiceApi.reducerPath]: requestServiceApi.reducer,
  [walletApi.reducerPath]: walletApi.reducer,
  [skillsApi.reducerPath]: skillsApi.reducer,
  [providerServiceAreaApi.reducerPath]: providerServiceAreaApi.reducer,
  [servicesQuestionApi.reducerPath]: servicesQuestionApi.reducer,
  [offerApi.reducerPath]: offerApi.reducer,
  [projectApi.reducerPath]: projectApi.reducer,
  [orderApi.reducerPath]: orderApi.reducer,
  [blogDataApi.reducerPath]: blogDataApi.reducer,
  [ticketApi.reducerPath]: ticketApi.reducer,
  [ticketMessageApi.reducerPath]: ticketMessageApi.reducer,
  [commentsApi.reducerPath]: commentsApi.reducer,
  [discountApi.reducerPath]: discountApi.reducer,
  [agreementApi.reducerPath]: agreementApi.reducer,
  [chatApi.reducerPath]: chatApi.reducer,
  [notificationsApi.reducerPath]: notificationsApi.reducer,
  [seoApi.reducerPath]: seoApi.reducer,
  [staticApi.reducerPath]: staticApi.reducer,
  [supportApi.reducerPath]: supportApi.reducer,
  [PromoteApi.reducerPath]: PromoteApi.reducer,
  [commissionApi.reducerPath]: commissionApi.reducer,
  [settlementApi.reducerPath]: settlementApi.reducer,
  [appVersionApi.reducerPath]: appVersionApi.reducer,

  [ProviderAvailabilityApi.reducerPath]: ProviderAvailabilityApi.reducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    })
      .concat(authApi.middleware)
      .concat(uploadApi.middleware)
      .concat(providerApi.middleware)
      .concat(addressApi.middleware)
      .concat(walletApi.middleware)
      .concat(providerDocsApi.middleware)
      .concat(requestServiceApi.middleware)
      .concat(serviceApi.middleware)
      .concat(faqApi.middleware)
      .concat(InviteApi.middleware)
      .concat(customerApi.middleware)
      .concat(skillsApi.middleware)
      .concat(providerServiceAreaApi.middleware)
      .concat(servicesQuestionApi.middleware)
      .concat(agreementApi.middleware)
      .concat(projectApi.middleware)
      .concat(orderApi.middleware)
      .concat(ticketApi.middleware)
      .concat(commentsApi.middleware)
      .concat(blogDataApi.middleware)
      .concat(ticketMessageApi.middleware)
      .concat(offerApi.middleware)
      .concat(discountApi.middleware)
      .concat(chatApi.middleware)
      .concat(appVersionApi.middleware)
      .concat(notificationsApi.middleware)
      .concat(seoApi.middleware)
      .concat(staticApi.middleware)
      .concat(supportApi.middleware)
      .concat(PromoteApi.middleware)
      .concat(commissionApi.middleware)
      .concat(settlementApi.middleware)
      .concat(ProviderAvailabilityApi.middleware)
      // بعدش middleware خطا رو اضافه کن
      .concat(rtkErrorMiddleware)
      .concat(socketMiddleware),
});

setupListeners(store.dispatch);

export const persistor = persistStore(store);
